// toast.tsx — 全局 Toast 辅助模块
// 允许在组件外(API client、事件处理器等)调用 toast 提示
//
// 使用方式:
//   import { toast } from '@/lib/toast';
//   toast.success('操作成功');
//   toast.error('出错了');
//
// 原理:
//   Fluent UI v9 的 Toaster 通过监听 document 上的 CustomEvent('fui-toast-show') 来渲染 Toast。
//   我们直接 dispatch 这个事件,完全绕过 useToastController hook 的限制,
//   无需 ToastBridge 组件,无需在组件内调用。

import type { ReactNode } from 'react';
import {
  Toast,
  ToastTitle,
  ToastBody,
  type ToastIntent,
} from '@fluentui/react-components';

let _counter = 0;

// 注意: Fluent UI v9 的 Toast timeout=0 不是"永不消失",
// 而是 setTimeout(fn, 0) —— 下一个宏任务立即 dismiss,表现为"一闪即灭"。
// 因此错误提示必须给一个足够长的可读时长,不能写 0。
const TIMEOUT_MS: Record<ToastIntent, number> = {
  error: 6000,
  warning: 4000,
  info: 4000,
  success: 3000,
};

function show(intent: ToastIntent, title: string, body?: string) {
  const content: ReactNode = (
    <Toast>
      <ToastTitle>{title}</ToastTitle>
      {body && <ToastBody>{body}</ToastBody>}
    </Toast>
  );

  const event = new CustomEvent('fui-toast-show', {
    detail: {
      content,
      toastId: `toast-${++_counter}`,
      toasterId: undefined,
      intent,
      timeout: TIMEOUT_MS[intent],
    },
  });
  document.dispatchEvent(event);
}

export const toast = {
  success: (msg: string, body?: string) => show('success', msg, body),
  error:   (msg: string, body?: string) => show('error', msg, body),
  info:    (msg: string, body?: string) => show('info', msg, body),
  warning: (msg: string, body?: string) => show('warning', msg, body),
};
